import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApplicationInsights } from "@packsize/application-monitoring";

import { NotificationBannerTemplate } from "./notification-banner.template";

const AUTO_DISMISS_TIMEOUT_MS = 30000;

interface UserInfo {
	ipAddress: string;
	location: string;
	timezone: string;
	locale: string;
	currency: string;
	city?: string;
	region?: string;
	country?: string;
	countryCode?: string;
	postalCode?: string;
	latitude?: string | number;
	longitude?: string | number;
	isp?: string;
}

const getUserInfo = async (): Promise<UserInfo> => {
	try {
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const locale = Intl.DateTimeFormat().resolvedOptions().locale;
		const currency = Intl.NumberFormat().resolvedOptions().currency || "unknown";

		let ipAddress = "unknown";
		let location = "unknown";
		let locationDetails: Partial<UserInfo> = {};

		try {
			const response = await fetch("https://ipapi.co/json/");
			if (response.ok) {
				const data = (await response.json()) as {
					ip?: string;
					city?: string;
					region?: string;
					country_name?: string;
					country_code?: string;
					postal?: string;
					latitude?: number;
					longitude?: number;
					org?: string;
				};
				ipAddress = data.ip || "unknown";
				location = `${data.city || "unknown"}, ${data.region || "unknown"}, ${data.country_name || "unknown"}`;
				locationDetails = {
					city: data.city || "unknown",
					region: data.region || "unknown",
					country: data.country_name || "unknown",
					countryCode: data.country_code || "unknown",
					postalCode: data.postal || "unknown",
					latitude: data.latitude || "unknown",
					longitude: data.longitude || "unknown",
					isp: data.org || "unknown",
				};
			}
		} catch (ipError) {
			console.warn("Could not fetch IP/location information:", ipError);
		}

		return {
			ipAddress,
			location,
			timezone,
			locale,
			currency,
			...locationDetails,
		};
	} catch (error) {
		console.error("Error getting user info:", error);
		return {
			ipAddress: "unknown",
			location: "unknown",
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown",
			locale: Intl.DateTimeFormat().resolvedOptions().locale || "unknown",
			currency: "unknown",
		};
	}
};

export const NotificationBanner = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [showBanner, setShowBanner] = useState(false);
	const [isTracked, setIsTracked] = useState(false);
	const [currentUrl, setCurrentUrl] = useState("");
	const appInsights = useApplicationInsights();

	useEffect(() => {
		const updateMe = searchParams.get("updateMe");
		if (updateMe === "true") {
			setShowBanner(true);

			const url = new URL(window.location.href);
			url.searchParams.delete("updateMe");
			setCurrentUrl(url.toString());

			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete("updateMe");
			setSearchParams(newSearchParams, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	const trackUserInfo = useCallback(async () => {
		try {
			const userInfo = await getUserInfo();
			const trackingData = {
				ipAddress: userInfo.ipAddress,
				location: userInfo.location,
				timezone: userInfo.timezone,
				timestamp: new Date().toISOString(),
				reason: "old_login_page_redirect",
				userAgent: navigator.userAgent,
				originalUrl: window.location.href,
				cleanUrl: currentUrl,
				referrer: document.referrer || "direct",
				screenResolution: `${screen.width}x${screen.height}`,
				viewportSize: `${window.innerWidth}x${window.innerHeight}`,
				language: navigator.language,
				languages: navigator.languages?.join(", ") || navigator.language,
				platform:
					(navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform ||
					navigator.platform ||
					"unknown",
				cookieEnabled: navigator.cookieEnabled,
				onlineStatus: navigator.onLine,
				connectionType:
					(navigator as { connection?: { effectiveType?: string } }).connection?.effectiveType || "unknown",
				deviceMemory: (navigator as { deviceMemory?: number }).deviceMemory || "unknown",
				hardwareConcurrency: navigator.hardwareConcurrency || "unknown",
			};

			appInsights.trackEvent({
				name: "OldWalmartLoginPageRedirect",
				properties: trackingData,
			});

			console.info("User tracking data:", trackingData);
		} catch (error) {
			console.error("Failed to track user information:", error);
			appInsights.trackException({
				exception: error as Error,
				properties: {
					context: "NotificationBanner_trackUserInfo",
					reason: "old_login_page_redirect",
				},
			});
		}
	}, [appInsights, currentUrl]);

	useEffect(() => {
		if (showBanner && !isTracked) {
			trackUserInfo();
			setIsTracked(true);
		}
	}, [showBanner, isTracked, trackUserInfo]);

	const dismissBanner = (): void => {
		setShowBanner(false);
	};

	useEffect(() => {
		if (showBanner) {
			const timer = setTimeout(() => {
				setShowBanner(false);
			}, AUTO_DISMISS_TIMEOUT_MS);

			return () => clearTimeout(timer);
		}
	}, [showBanner]);

	if (showBanner === false) {
		return null;
	}

	return <NotificationBannerTemplate currentUrl={currentUrl} onDismiss={dismissBanner} />;
};
